import apiService from './api';

// Request/Response types
export interface RegistrationRequest {
  username: string;
  password: string;
  confirmPassword: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  referralCode?: string;
  referralSource?: string;
  accountType?: string;
  acceptTerms: boolean;
  acceptPrivacy: boolean;
}

export interface RegistrationResponse {
  success: boolean;
  userId?: string;
  username?: string;
  message?: string;
  emailVerificationSent?: boolean;
  smsVerificationSent?: boolean;
  requiresEmailVerification?: boolean;
  referralSchedule?: string;
}

export interface EmailVerificationRequest {
  email: string;
  code: string;
}

export interface EmailVerificationResponse {
  success: boolean;
  message?: string;
  newVerificationLevel?: number;
}

export interface UsernameAvailability {
  username: string;
  available: boolean;
  reason?: string;
}

export interface EmailAvailability {
  email: string;
  available: boolean;
  reason?: string;
}

export interface VerificationCodeRequest {
  verificationType: 'EMAIL' | 'PHONE';
  code: string;
}

export interface VerificationStatus {
  userId: string;
  username: string;
  verificationLevel: number;
  verificationLevelName: string;
  isEmailVerified: boolean;
  emailVerifiedDate?: string;
  phoneNumber?: string;
  isPhoneVerified: boolean;
  phoneVerifiedDate?: string;
  isIdVerified: boolean;
  idVerifiedDate?: string;
  idVerificationProvider?: string;
  isAddressVerified: boolean;
  addressVerifiedDate?: string;
}

class RegistrationService {
  /**
   * Check if username is available
   */
  async checkUsername(username: string, email?: string): Promise<UsernameAvailability> {
    const params = email ? `?email=${encodeURIComponent(email)}` : '';
    return apiService.get<UsernameAvailability>(`/api/Registration/check-username/${encodeURIComponent(username)}${params}`);
  }

  /**
   * Check if email is available
   */
  async checkEmail(email: string): Promise<EmailAvailability> {
    return apiService.get<EmailAvailability>(`/api/Registration/check-email/${encodeURIComponent(email)}`);
  }

  /**
   * Register a new user
   */
  async register(request: RegistrationRequest): Promise<RegistrationResponse> {
    return apiService.post<RegistrationResponse>('/api/Registration/register', request);
  }

  /**
   * Request email verification code (requires auth)
   */
  async requestEmailVerification(email: string): Promise<{ message: string }> {
    return apiService.post<{ message: string }>('/api/Registration/verify/email/request', { email });
  }

  /**
   * Request phone verification code (requires auth)
   */
  async requestPhoneVerification(phoneNumber: string): Promise<{ message: string }> {
    return apiService.post<{ message: string }>('/api/Registration/verify/phone/request', { phoneNumber });
  }

  /**
   * Submit verification code (requires auth)
   */
  async submitVerificationCode(request: VerificationCodeRequest): Promise<{ message: string; verificationLevel?: number }> {
    return apiService.post<{ message: string; verificationLevel?: number }>('/api/Registration/verify/submit', request);
  }

  /**
   * Get current verification status (requires auth)
   */
  async getVerificationStatus(): Promise<VerificationStatus> {
    return apiService.get<VerificationStatus>('/api/Registration/verify/status');
  }

  /**
   * Verify email with code (public - no auth required)
   * This is used for new registration email verification
   */
  async verifyEmailPublic(email: string, code: string): Promise<EmailVerificationResponse> {
    return apiService.post<EmailVerificationResponse>('/api/Registration/verify/email/public', { email, code });
  }

  /**
   * Resend verification code to email (public - no auth required)
   */
  async resendVerificationCode(email: string): Promise<{ message: string }> {
    return apiService.post<{ message: string }>('/api/Registration/verify/email/resend', { email });
  }
}

export const registrationService = new RegistrationService();
export default registrationService;
